import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

import { BACKEND_URL } from '../config/api';

// Hardcoded defaults ensure selects render immediately on iOS
// before the API response arrives. DB is the source of truth long-term.
const DEFAULT_CATEGORIES = [
  { value: 'cardstock', label: 'Cardstock' },
  { value: 'patterned_paper', label: 'Patterned Paper' },
  { value: 'die', label: 'Die Cut' },
  { value: 'stamp', label: 'Stamp' },
  { value: 'stamp_pad', label: 'Stamp Pad' },
  { value: 'stencil', label: 'Stencil' },
  { value: 'embossing_folder', label: 'Embossing Folder' },
  { value: 'embellishment', label: 'Embellishment' },
  { value: 'sentiment', label: 'Sentiment' },
  { value: 'sticker', label: 'Sticker' },
  { value: 'other', label: 'Other' },
];

const CategoryContext = createContext(null);

export const CategoryProvider = ({ children }) => {
  const { user, getAuthHeaders } = useAuth();
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [categoriesWithAll, setCategoriesWithAll] = useState([
    { value: '', label: 'All Categories' },
    ...DEFAULT_CATEGORIES
  ]);
  const [loaded, setLoaded] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/stash/categories/list`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.clone().json();
        const cats = data.categories || [];
        if (cats.length > 0) {
          setCategories(cats);
          setCategoriesWithAll([{ value: '', label: 'All Categories' }, ...cats]);
        }
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      // Keep defaults on error — dropdowns still work
    } finally {
      setLoaded(true);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (user) {
      fetchCategories();
    }
  }, [user, fetchCategories]);

  return (
    <CategoryContext.Provider value={{ categories, categoriesWithAll, loaded, refetch: fetchCategories }}>
      {children}
    </CategoryContext.Provider>
  );
};

export const useCategories = () => {
  const ctx = useContext(CategoryContext);
  if (!ctx) {
    return {
      categories: DEFAULT_CATEGORIES,
      categoriesWithAll: [{ value: '', label: 'All Categories' }, ...DEFAULT_CATEGORIES],
      loaded: false,
      refetch: () => {}
    };
  }
  return ctx;
};
